import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/CloudUpload";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  getEmployeeBasicInfo,
  updateEmployeeBasicInfo,
  getDropdownDesignations,
  getDropdownDepartments,
  uploadEMployeePicture,
  getEmployeePictureInfo,
  deleteEmployeeProfilePicture,
} from "../../../services/Services";
import { useParams } from "react-router-dom";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import { baseURL } from "app/Api/axios";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const validationSchema = Yup.object().shape({
  date_of_birth: Yup.date()
    .max(new Date(), "Date of birth cannot be in future")
    .required("Date of birth is required"),
  personal_email: Yup.string()
    .email("Invalid email")
    .required("Personal email is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  department_id: Yup.string().required("Department is required"),
  designation_id: Yup.string().required("Designation is required"),
  employment_type: Yup.string()
    .oneOf(["Full-time", "Part-time", "Contract", "Intern"])
    .required("Employment type is required"),
});

const BasicInfo = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [deletingPicture, setDeletingPicture] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeId] = useState(id);

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);

  const isEdit = !!existingData;

  useEffect(() => {
    const fetchData = async () => {
      await loadDropdownData();
      loadExistingData();
      await getProfilePicture();
    };

    fetchData();
    setPicturePreview(null);
  }, [id]);

  const getProfilePicture = async () => {
    try {
      const response = await getEmployeePictureInfo(id);
      if (response?.status === 200) {
        setPictureFile(response?.data?.data?.picture);
        const picturePath = response?.data?.data?.picture;
        setPicturePreview(picturePath ? `${baseURL}${picturePath}` : null);
      }
    } catch (err) {
    } finally {
      setDropdownLoading(false);
      setPictureFile(null);
    }
  };

  const loadDropdownData = async () => {
    try {
      setDropdownLoading(true);

      const [deptResponse, desigResponse] = await Promise.all([
        getDropdownDepartments(),
        getDropdownDesignations(),
      ]);

      if (deptResponse?.status === 200) {
        setDepartments(deptResponse.data?.data || []);
      }

      if (desigResponse?.status === 200) {
        setDesignations(desigResponse.data?.data || []);
      }
    } catch (err) {
      console.error("Error loading dropdowns:", err);
      generateErrorMessage("Failed to load departments/designations");
    } finally {
      setDropdownLoading(false);
    }
  };

  const loadExistingData = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeBasicInfo(id);
      if (response?.status === 200) {
        const data = response.data?.data || null;
        setExistingData(data);
        if (data?.picture) {
          setPicturePreview(`${baseURL}${data.picture}`);
        } else {
          setPicturePreview(null);
        }
      } else {
        setExistingData(null);
      }
    } catch (err) {
      console.error(err);
      setExistingData(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePictureUpload = async (file) => {
    if (!file || !employeeId) return;

    try {
      setUploadingPicture(true);

      const formData = new FormData();
      formData.append("picture", file);
      formData.append("employeeId", employeeId);
      const response = await uploadEMployeePicture(formData, employeeId);

      if (response?.status === 200) {
        generateSuccessMessage("Picture uploaded successfully");
        const pictureUrl = response.data?.data?.fullUrl;
        setPicturePreview(pictureUrl);
        setPictureFile(null);
        await loadExistingData();
      }
    } catch (err) {
      console.error("Picture upload error:", err);
      generateErrorMessage(
        err.response?.data?.message || "Failed to upload picture"
      );
      setPictureFile(null);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handlePictureDelete = async () => {
    if (picturePreview) {
      URL.revokeObjectURL(picturePreview);
      setPicturePreview(null);
      setPictureFile(null);
      setDeleteDialogOpen(false);
    }
    if (!employeeId) return;
    try {
      setDeletingPicture(true);
      setDeleteDialogOpen(false);

      const response = await deleteEmployeeProfilePicture(employeeId);

      if (response?.status === 200) {
        generateSuccessMessage("Picture deleted successfully");
        setPicturePreview(null);
        setPictureFile(null);
        setUploadingPicture(null);
        await loadExistingData();
      }
    } catch (err) {
      console.error("Picture delete error:", err);
    } finally {
      setDeletingPicture(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        generateErrorMessage("Only PNG, JPG, and JPEG images are allowed");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        generateErrorMessage("File size must be less than 5 MB");
        return;
      }
      setPictureFile(file);

      const reader = new FileReader();
      reader.onload = () => setPicturePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const getInitialValues = () => {
    if (isEdit && existingData) {
      return {
        date_of_birth: existingData.date_of_birth || "",
        personal_email: existingData.personal_email || "",
        mobile: existingData.mobile || "",
        department_id: existingData.department_id || "",
        designation_id: existingData.designation_id || "",
        employment_type: existingData.employment_type || "Full-time",
      };
    }
    return {
      date_of_birth: "",
      personal_email: "",
      mobile: "",
      department_id: "",
      designation_id: "",
      employment_type: "Full-time",
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("dateOfBirth", values.date_of_birth);
      formData.append("personalEmail", values.personal_email);
      formData.append("mobile", values.mobile);
      formData.append("departmentId", values.department_id);
      formData.append("designationId", values.designation_id);
      formData.append("employmentType", values.employment_type);

      const response = await updateEmployeeBasicInfo(employeeId, formData);

      if (response?.status === 200) {
        generateSuccessMessage("Employee information saved successfully");
        await loadExistingData();
        if (onCompleted) onCompleted(values);
      }
    } catch (err) {
      console.error("Submit error:", err);
      generateErrorMessage(
        err.response?.data?.message || "Failed to save employee information"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || dropdownLoading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label={dropdownLoading ? "Loading form data…" : "Loading employee data…"} />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load employee data." onRetry={loadExistingData} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <Box sx={{ ...sectionCardSx, display: "flex", flexDirection: "column", gap: "18px" }}>
              <SectionHeader
                title="Basic information"
                sub="Personal contact details and organisational assignment."
                action={<StatusChip label={isEdit ? "Editing" : "New"} tone="brand" />}
              />

              <Grid container spacing={3}>
                {/* Profile picture */}
                <Grid item xs={12} md={4}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Box position="relative">
                      <Avatar
                        src={picturePreview}
                        sx={{ width: 108, height: 108, borderRadius: "18px", bgcolor: T.hairline, color: T.muted }}
                      >
                        {!picturePreview && <PersonIcon sx={{ fontSize: 52 }} />}
                      </Avatar>

                      {picturePreview && picturePreview !== "null" && (
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute", top: -6, right: -6,
                            bgcolor: T.danger, color: "#fff", width: 28, height: 28,
                            "&:hover": { bgcolor: T.dangerText },
                          }}
                          onClick={() => setDeleteDialogOpen(true)}
                          disabled={deletingPicture || uploadingPicture}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                      <SecondaryButton component="label" sx={{ textAlign: "center" }} disabled={uploadingPicture || deletingPicture}>
                        <UploadIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: "6px" }} />
                        Select photo
                        <input
                          type="file"
                          hidden
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={handleFileSelect}
                        />
                      </SecondaryButton>

                      {pictureFile && (
                        <>
                          <Box sx={{ fontSize: "12px", color: T.muted, textAlign: "center" }}>
                            Selected: {pictureFile.name}
                          </Box>
                          <PrimaryButton
                            type="button"
                            onClick={() => handlePictureUpload(pictureFile)}
                            disabled={uploadingPicture || !employeeId}
                          >
                            {uploadingPicture ? "Uploading…" : "Upload picture"}
                          </PrimaryButton>
                          <SecondaryButton
                            onClick={() => {
                              setPictureFile(null);
                              if (existingData?.picture) {
                                setPicturePreview(`${baseURL}${existingData.picture}`);
                              } else {
                                setPicturePreview(null);
                              }
                            }}
                            disabled={uploadingPicture}
                          >
                            Cancel
                          </SecondaryButton>
                        </>
                      )}
                    </Box>

                    <Box sx={{ fontSize: "11.5px", color: T.muted, textAlign: "center" }}>
                      Allowed: PNG, JPG, JPEG (max 5MB)
                    </Box>
                  </Box>
                </Grid>

                {/* Form fields */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="date_of_birth"
                        label="Date of birth"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={values.date_of_birth}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.date_of_birth && Boolean(errors.date_of_birth)}
                        helperText={touched.date_of_birth && errors.date_of_birth}
                        disabled={isSubmitting}
                        inputProps={{ max: new Date().toISOString().split("T")[0] }}
                        sx={muiFieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="personal_email"
                        label="Personal email"
                        type="email"
                        value={values.personal_email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.personal_email && Boolean(errors.personal_email)}
                        helperText={touched.personal_email && errors.personal_email}
                        disabled={isSubmitting}
                        sx={muiFieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="mobile"
                        label="Mobile"
                        type="tel"
                        value={values.mobile}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.mobile && Boolean(errors.mobile)}
                        helperText={touched.mobile && errors.mobile}
                        disabled={isSubmitting}
                        sx={muiFieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" error={touched.department_id && Boolean(errors.department_id)} disabled={isSubmitting} sx={muiFieldSx}>
                        <InputLabel>Department</InputLabel>
                        <Select
                          name="department_id"
                          value={values.department_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Department"
                        >
                          {departments.length === 0 ? (
                            <MenuItem value=""><em>No departments available</em></MenuItem>
                          ) : (
                            departments.map((dept) => (
                              <MenuItem key={dept.depId} value={dept.depId}>{dept.departmentName}</MenuItem>
                            ))
                          )}
                        </Select>
                        {touched.department_id && errors.department_id && (
                          <FormHelperText>{errors.department_id}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" error={touched.designation_id && Boolean(errors.designation_id)} disabled={isSubmitting} sx={muiFieldSx}>
                        <InputLabel>Designation</InputLabel>
                        <Select
                          name="designation_id"
                          value={values.designation_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Designation"
                        >
                          {designations.length === 0 ? (
                            <MenuItem value=""><em>No designations available</em></MenuItem>
                          ) : (
                            designations.map((desig) => (
                              <MenuItem key={desig.desId} value={desig.desId}>{desig.designationName}</MenuItem>
                            ))
                          )}
                        </Select>
                        {touched.designation_id && errors.designation_id && (
                          <FormHelperText>{errors.designation_id}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" error={touched.employment_type && Boolean(errors.employment_type)} disabled={isSubmitting} sx={muiFieldSx}>
                        <InputLabel>Employment type</InputLabel>
                        <Select
                          name="employment_type"
                          value={values.employment_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Employment type"
                        >
                          <MenuItem value="Full-time">Full-time</MenuItem>
                          <MenuItem value="Part-time">Part-time</MenuItem>
                          <MenuItem value="Contract">Contract</MenuItem>
                          <MenuItem value="Intern">Intern</MenuItem>
                        </Select>
                        {touched.employment_type && errors.employment_type && (
                          <FormHelperText>{errors.employment_type}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" gap={2}>
                        <PrimaryButton disabled={isSubmitting}>
                          {isSubmitting ? "Saving…" : isEdit ? "Update details" : "Save details"}
                        </PrimaryButton>
                        {isEdit && (
                          <SecondaryButton onClick={loadExistingData} disabled={isSubmitting}>
                            Refresh
                          </SecondaryButton>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>

      {/* Delete picture confirmation (local dialog — not the cross-module ConfirmModal) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: T.cardR, fontFamily: T.body_f } }}
      >
        <DialogTitle sx={{ fontFamily: T.heading, fontSize: "17px", fontWeight: 600 }}>Delete picture</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: "13.5px", color: T.body2 }}>
            Are you sure you want to delete this profile picture? This action cannot be undone.
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 20px 18px" }}>
          <SecondaryButton onClick={() => setDeleteDialogOpen(false)} disabled={deletingPicture}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handlePictureDelete}
            disabled={deletingPicture}
            sx={{ background: T.danger, boxShadow: "none" }}
          >
            {deletingPicture ? "Deleting…" : "Delete"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BasicInfo;
